// Copyright (c) 2020-2023 Visual Intelligence LP. All rights reserved.
//
import { injectable } from 'inversify';
import { observable, computed, action, makeObservable } from 'mobx';

import container from '@core/di';
import BaseStore from '@core/stores/base';

import TimelineDataService from '@shared/services/timeline-data';
import TimelineData, { TimelineDataDTO } from '@shared/models/jobrequesttimelines/timeline-data';
import { LineBlockDTO } from '@shared/models/jobrequesttimelines';
import { IdsFromFormDTO, BlockOption } from '@shared/models/jobrequesttimelines/lineblock';
import { NewTimelineDTO, FormListDTO } from '@shared/models/jobrequesttimelines/new-timeline';
import { Id } from '@shared/types/common';



// Expose methods provided by the service component.
@injectable()
export default class TimelineDataStore extends BaseStore<TimelineDataDTO, TimelineData, TimelineDataService> {
  static diToken = Symbol('jrtimelines-store');
  // stateful arrays for customer/order/asset data.
  @observable _listCustomer: Array<FormListDTO> = [];
  @observable _listOrder: Array<FormListDTO> = [];
  @observable _listAsset: Array<FormListDTO> = [];
  @observable _listVendor: Array<FormListDTO> = [];
  @observable _listSelected: Array<FormListDTO> = [];

  constructor() {
    super({ service: container.get<TimelineDataService>(TimelineDataService.diToken) }); // get a handle to object using DI.
    makeObservable(this);
  }

  // Update a timeline.
  // Input:
  //   job request id.
  //   data: timeline blocks.
  // Return:
  //   An updated timeline.
  editJRTimelines = (jobRequestId: string | number, data: LineBlockDTO, ids: IdsFromFormDTO | undefined): Promise<TimelineData | undefined> => {
    return this.service.updateJRTimeline(jobRequestId, data, ids);
  }

  // Add a JR time line.
  // Input:
  //   newTimeline: ids for customer/order/asset/type.
  // Return:
  //   A new timeline.
  addJRTimeline = (newTimeline: NewTimelineDTO): Promise<TimelineData | undefined> => {
    return this.service.addJRTimeline(newTimeline);
  }

  // Get asset/collection/tech/status... Ids for a job request.
  // Input:
  //   jrOption: job request text and Id.
  getSitePropertyIds = (jrNameNId: BlockOption): Promise<Array<BlockOption>> => {
    return this.service.getSitePropertiesIds(jrNameNId);
  }

  // Fetch the lists the the form fields (Customer/Order/Asset/...) from portal-api.
  // Note: caller will use the 'get' method for a list name to access the individual lists.
  // Input:
  //   query: array of id's or partial names to query for data.  
  // Return:
  //   Array of lists for the form fields.
  @action getListsForNewJRForm = async (query: FormListDTO[]): Promise<Array<Array<{id: Id, name: string}>> | undefined> => {
    this.loading.list = true;

    try {
      const dataFromRest = await this.service.getListsForNewJRForm(query);

      // Parse the array of lists into the lists for loading into the form.
      if(dataFromRest !== undefined && dataFromRest.length > 3) {
        this._listCustomer = dataFromRest[0];
        this._listOrder = dataFromRest[1];
        this._listAsset = dataFromRest[2];
        this._listVendor = dataFromRest[4];
        this._listSelected = dataFromRest[3];
      }
      
      this.loading.list = false;

      console.log("**** getListsForNewJRForm.  Req/Res follow:");
      console.log(query)
      console.log(dataFromRest);
      return dataFromRest;
    } 
    catch (err) {
      this.loading.list = false;
    }
  }

  // Return the customer/order/asset lists as props.
  @computed get listCustomer() {
    return this._listCustomer;
  }
  @computed get listOrder() {
    return this._listOrder;
  }
  @computed get listAsset() {
    return this._listAsset;
  }
  @computed get listVendor() {
    return this._listVendor;
  }

  // Clear the stateful lists.
  @action reset() {
    this._listCustomer = [];
    this._listOrder = [];
    this._listAsset = [];
    this._listVendor = [];
  }
}